import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLink } from '@angular/router';
import { PermissionDirective } from '@app/core/directives/permission.directive';
import { Events } from '@app/core/models/event-map';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { CopyWebquotationComponent } from '@app/shared/components/modals/copy-webquotation/copy-webquotation.component';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { accessLevel } from '@app/shared/models/role-enum';
import { CatalogueService } from '@app/shared/services/internal-services/catalogue-internal.service';
import { ConstantsService } from '@app/shared/services/internal-services/constants-internal.service';
import { PortalApiService } from '@app/shared/services/portal-api.service';
import { UserService } from '@app/shared/services/User.Service';
import { UserContextSelectors } from '@app/shared/state/user-context/user-context.selectors';
import { environment } from '@environments/environment';
import { Store } from '@ngxs/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
  catchError,
  concat,
  interval,
  map,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';

@Component({
  selector: 'kp-left-nav',
  imports: [
    CopyWebquotationComponent,
    NgClass,
    PermissionDirective,
    RouterLink,
  ],
  templateUrl: './left-nav.component.html',
  styleUrl: './left-nav.component.scss',
})
export class LeftNavComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef);
  #store = inject(Store);
  #subscriptions: Subscription[] = [];

  #catalogueInternalService = inject(CatalogueService);
  #modalService = inject(BsModalService);
  #publishSubscribeService = inject(PublishSubscribeService);

  isAuthenticated = this.#store.selectSignal(UserContextSelectors.isAuthenticated)
  isMf = this.#store.selectSignal(UserContextSelectors.isMf)
  isReseller = this.#store.selectSignal(UserContextSelectors.isReseller)
  isAssembler = this.#store.selectSignal(UserContextSelectors.isAssembler)

  mfInfoUrl = signal<string>(environment.iframeInformationMf)

  private aftermarketPermission = this.#store.selectSignal(UserContextSelectors.hasPermission('Aftermarket', 'aftermarket_role'))
  private userRole = this.#store.selectSignal(UserContextSelectors.userRole)

  isTechnician = computed(() => {
    const perm = this.aftermarketPermission()
    const role = this.userRole()
    return perm?.value === 'TECHNICIAN' || role == accessLevel.AFTERMARKET_TECHNICIAN
  })

  readonly webQuotationsTitle   = computed(() => this.isReseller() ? 'Webofferter'       : 'Affärsförslag');
  readonly newWebQuotationTitle = computed(() => this.isReseller() ? 'Ny Weboffert'      : 'Nytt Affärsförslag');
  readonly openWebQuotationsTitle = computed(() => this.isReseller() ? 'Öppna offerter'  : 'Öppna Affärsförslag');

  count_newSnicAppUserRelations = signal<number>(0);
  count_snicAppSalesBasketsInNeedOfAction = signal<number>(0);
  count_windowSelectorLeadsInNeedOfAction = signal<number>(0);
  count_windowBuilderLeadsInNeedOfAction = signal<number>(0);
  count_salesOrdersInNeedOfAction = signal<number>(0);
  count_salesTableNeedRebooking = signal<number>(0);
  count_LeadsInNeedOfAction = signal<number>(0);

  text_newSnicAppUserRelations = signal<string>('');
  text_snicAppSalesBasketsInNeedOfAction = signal<string>('');
  text_windowSelectorLeadsInNeedOfAction = signal<string>('');
  text_salesOrdersInNeedOfAction = signal<string>('');
  text_LeadsInNeedOfAction = signal<string>('');

  snickarAppName: string;
  snickarAppNameUCFirst: string;
  snickarAppNameShort: string;

  copyWebquotationModalId: string = 'copyWebquotationModal';

  private stopPolling$ = new Subject<void>();
  private pollingSub?: Subscription;
  readonly ACTIONS_COUNT_POLLING_INTERVAL = 60000;

  portalUser: PortalUser = undefined

  enableNavbar = computed(() => {
    const isAuthenticated = this.isAuthenticated()
    const isTechnician = this.#store.selectSnapshot(UserContextSelectors.hasPermission('Aftermarket', 'aftermarket_role'))?.value == 'TECHNICIAN'
    const isAssembler = this.isAssembler()
    return isAuthenticated && !isTechnician && !isAssembler
  })

  constructor(
    private userInternalService: UserService,
    private router: Router,
    private portalApiService: PortalApiService
  ) {
    this.snickarAppName = environment.snickarAppName;
    this.snickarAppNameUCFirst = environment.snickarAppNameUCFirst;
    this.snickarAppNameShort = environment.snickarAppNameShort;
    this.snickarAppNameShort =
      this.snickarAppNameShort == null || this.snickarAppNameShort == ''
        ? 'Mobilappen'
        : this.snickarAppNameShort;

    effect(() => {
      const isAssembler = this.isAssembler()
      let el: HTMLElement = document.getElementById('content-wrapper')
      const hasPageContentWrapperClass = el.classList.contains('page-content-wrapper')

      if(hasPageContentWrapperClass && isAssembler) {
        el.classList.remove('page-content-wrapper')
      } else if(!hasPageContentWrapperClass && !isAssembler){
        el.classList.add('page-content-wrapper')
      }
    })
  }

  ngOnInit(): void {

    this.registerEventSubscriptions();

    this.userInternalService.portalUser$
    .pipe(takeUntilDestroyed(this.#destroyRef))
    .subscribe({
      next: (user: PortalUser) => {
        if(user) {
          this.portalUser = user
          this.handleEventUserLoggedInOrOut()
        }
      }
    })
  }

  private registerEventSubscriptions(): void {

    this.#subscriptions.push(
      this.#publishSubscribeService
        .on(Events.UserService.USER_LOGGED_OUT)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => {
            this.handleEventUserLoggedInOrOut();
          },
        })
    );
  }

  isAftermarket(): boolean {
    return ['MANAGER', 'TECHNICIAN', 'ADMIN'].includes(this.aftermarketPermission().value) || this.userRole() === accessLevel.AFTERMARKET_TECHNICIAN
  }

  mainMenuClick(e) {
    if (e.target.classList.contains('main-menu-toggler')) {
      const wrapper = document.getElementById('wrapper');
      wrapper.classList.contains('toggled')
        ? wrapper.classList.remove('toggled')
        : wrapper.classList.add('toggled');
    }
  }

  isMainMenuMinimized() {
    const wrapper = document.getElementById('wrapper');
    return wrapper === null ? true : wrapper.classList.contains('toggled');
  }

  openCreateReclaim(copyWebquotationModal: TemplateRef<void>) {
    this.#modalService.show(copyWebquotationModal, {
      id: this.copyWebquotationModalId,
      class: 'modal-lg',
      backdrop: 'static',
    });
  }

  /**
   * EventEmitter from copyWebquotationModal
   * @param data data emitted
   */
  copyWebquotationModalEvent(
    data:
      | { newSalesBasketId: string; displayConvertMessages: boolean }
      | undefined
  ) {
    this.#modalService.hide(this.copyWebquotationModalId);

    if (data) {
      this.loadWebQuotation(data.newSalesBasketId);
    }
  }

  private loadWebQuotation(salesBasketId: string) {
    this.router.navigate([`/webquotation`, salesBasketId]);
  }

  currentActive(href: string) {
    return this.router.url === (href.startsWith('/') ? href : '/' + href);
  }

  private startPolling() {
    const {
      SVFACCOUNT,
      CUST_ACCOUNTNUM_SP,
      CUST_ACCOUNTNUM_TR,
      CUST_ACCOUNTNUM_CC,
    } = this.portalUser ?? {}

    const polling$ = concat(
      of(0), // start with immediate call
      interval(this.ACTIONS_COUNT_POLLING_INTERVAL) // later calls are done with each interval
    ).pipe(
      switchMap(() =>
        this.portalApiService
          .getActionsCount(
            SVFACCOUNT,
            CUST_ACCOUNTNUM_SP,
            CUST_ACCOUNTNUM_TR,
            CUST_ACCOUNTNUM_CC
          )
          .pipe(
            map((data) => {
              this.handleActionsCountResponse(data);
              return data;
            }),
            catchError((err) => {
              console.error('Polling error:', err);
              return of(null); // handle error and continue polling
            })
          )
      ),
      takeUntil(this.stopPolling$)
    );

    return polling$;
  }

  private handleActionsCountResponse(data?: any): void {
    if (!data) {
      this.count_newSnicAppUserRelations.set(0);
      this.count_snicAppSalesBasketsInNeedOfAction.set(0);
      this.text_newSnicAppUserRelations.set('');
      this.text_snicAppSalesBasketsInNeedOfAction.set('');
      this.count_windowSelectorLeadsInNeedOfAction.set(0);
    } else {
      this.count_newSnicAppUserRelations.set(
        data.count_newSnicAppUserRelations
      );
      this.count_snicAppSalesBasketsInNeedOfAction.set(
        data.count_snicAppSalesBasketsInNeedOfAction
      );
      this.count_windowSelectorLeadsInNeedOfAction.set(
        data.count_windowSelectorLeadsInNeedOfAction
      );
      this.count_windowBuilderLeadsInNeedOfAction.set(
        data.count_windowBuilderLeadsInNeedOfAction
      );
      this.count_LeadsInNeedOfAction.set(
        this.isMf()
          ? this.count_windowBuilderLeadsInNeedOfAction()
          : this.count_windowSelectorLeadsInNeedOfAction()
      );
      this.count_salesOrdersInNeedOfAction.set(
        data.count_salesOrdersInNeedOfAction
      );
      this.text_salesOrdersInNeedOfAction.set('');

      this.text_LeadsInNeedOfAction.set(
        this.count_LeadsInNeedOfAction() > 0
          ? `${this.count_LeadsInNeedOfAction()} leads`
          : ''
      );

      if (this.count_salesOrdersInNeedOfAction() > 0) {
        this.text_salesOrdersInNeedOfAction.set(
          `Det finns ${this.count_salesOrdersInNeedOfAction()} ordrar att åtgärda`
        );
      }

      if (this.count_windowSelectorLeadsInNeedOfAction() > 0) {
        this.text_windowSelectorLeadsInNeedOfAction.set(
          `Det finns ${this.count_windowSelectorLeadsInNeedOfAction()} lead${
            this.count_windowSelectorLeadsInNeedOfAction() > 1 ? 's ' : ' '
          } från Fönsterväljaren`
        );
      } else {
        this.text_windowSelectorLeadsInNeedOfAction.set('');
      }

      if (this.count_snicAppSalesBasketsInNeedOfAction() > 0) {
        this.text_snicAppSalesBasketsInNeedOfAction.set(
          `${this.count_snicAppSalesBasketsInNeedOfAction()} offert ${
            this.count_snicAppSalesBasketsInNeedOfAction() > 1 ? 'er' : ''
          } från ${this.snickarAppName} väntar på åtgärd`
        );
      } else {
        this.text_snicAppSalesBasketsInNeedOfAction.set('');
      }

      if (this.count_newSnicAppUserRelations() <= 0) {
        this.text_newSnicAppUserRelations.set('');
      } else if (this.count_newSnicAppUserRelations() == 1) {
        this.text_newSnicAppUserRelations.set(
          `${this.count_newSnicAppUserRelations()} ny förfrågning från ${
            this.snickarAppName
          } väntar på åtgärd`
        );
      } else if (this.count_newSnicAppUserRelations() > 1) {
        this.text_newSnicAppUserRelations.set(
          `${this.count_newSnicAppUserRelations()} nya förfrågningar från ${
            this.snickarAppName
          } väntar på åtgärd`
        );
      }
    }
  }

  onNewWebQuotation() {
    const preSelectedBrand = this.userInternalService.getPreSelectedBrand();

    const newWebQuotationStartsAt =
      this.userInternalService.getNewWebQuotationStartsAt();

    if (
      preSelectedBrand &&
      newWebQuotationStartsAt ===
        ConstantsService.NewWebQuotationStartsAt.ENUM_EMPTY_WEB_QUOTATION
    ) {
      // Create salesBasket
      this.#catalogueInternalService
        .createSalesBasket(preSelectedBrand)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (response) => {
            const axResponse = response;

            if (axResponse.AllOk) {
              this.router
                .navigate([`/start`], { replaceUrl: false })
                .then(() => {
                  this.router.navigate(
                    [`/webquotation`, axResponse.SalesBasketId],
                    { replaceUrl: true }
                  );
                });
            }
          },
          error: (err) => {
            this.router.navigate(['webquotation', 'catalogue', 0]);
          },
        });
    } else if (
      newWebQuotationStartsAt ===
      ConstantsService.NewWebQuotationStartsAt.ENUM_SELECT_FIRST_ITEM
    ) {
      this.router.navigate(['webquotation', 'catalogue', 0]);
    }
  }

  /** Display or hide lef-nav when user logs in or out, also if user is logged in actionsData is fetched */
  private handleEventUserLoggedInOrOut() {
    if (this.isAuthenticated() && !this.isAssembler()) {
      this.stopPolling$.next();
      this.pollingSub?.unsubscribe();

      this.pollingSub = this.startPolling()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe();
    } else {
      this.stopPolling$.next();
      this.pollingSub?.unsubscribe();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling$.next(null);
    this.stopPolling$.complete();
  }
}
